import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../../components/admin/PageHeader";
import { DataTable } from "../../../components/admin/DataTable";
import { FormModal } from "../../../components/admin/FormModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useCrudList } from "../../../hooks/useCrudList";
import { brandsService } from "../services/brands";

function formatDate(iso, lang) {
  if (!iso) return "";
  const locale = lang?.startsWith("en") ? "en-GB" : "de-DE";
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export default function BrandsPage() {
  const { t, i18n } = useTranslation();
  const crud = useCrudList({
    service: brandsService,
    defaultSort: { key: "name", dir: "asc" },
    sessionKey: "brands.pageSize",
  });
  const [formValue, setFormValue] = useState({ name: "" });

  useEffect(() => {
    if (!crud.modalOpen) return;
    setFormValue({ name: crud.editingRow?.name || "" });
  }, [crud.modalOpen, crud.editingRow]);

  // Phase A11: description column removed — the table now shows just name +
  // creation date, keeping the two remaining columns comfortably spaced.
  const columns = [
    {
      key: "name",
      labelKey: "admin.brands.col_name",
      sortable: true,
      className: "w-2/3",
    },
    {
      key: "created_at",
      labelKey: "admin.common.created_at",
      sortable: true,
      className: "w-40 whitespace-nowrap",
      render: (r) => (
        <span className="font-mono text-xs text-text-secondary">
          {formatDate(r.created_at, i18n.language)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="brands-page">
        <PageHeader
          title={t("admin.brands.title")}
          subtitle={t("admin.brands.subtitle")}
          actionLabel={t("admin.common.add_new")}
          onAction={crud.openCreate}
          actionTestId="brands-add-new"
        />
        <DataTable
          columns={columns}
          items={crud.items}
          loading={crud.loading}
          total={crud.total}
          page={crud.page}
          pageSize={crud.pageSize}
          onPageChange={crud.setPage}
          onPageSizeChange={crud.setPageSize}
          sortKey={crud.sortKey}
          sortDir={crud.sortDir}
          onSortChange={crud.changeSort}
          searchValue={crud.searchValue}
          onSearchChange={crud.setSearchValue}
          onEdit={crud.openEdit}
          onDelete={crud.askDelete}
          entityTestId="brands"
        />
      </div>

      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        mode={crud.editingRow ? "edit" : "create"}
        entityLabel={t("admin.brands.entity_singular")}
        saving={crud.saving}
        onSubmit={() =>
          crud.save({ name: formValue.name.trim() })
        }
        testId="brands-form"
        submitTestId="brands-form-submit"
        cancelTestId="brands-form-cancel"
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="brands-form-name">
              {t("admin.brands.field_name")}
            </Label>
            <Input
              id="brands-form-name"
              value={formValue.name}
              onChange={(e) =>
                setFormValue((v) => ({ ...v, name: e.target.value }))
              }
              required
              minLength={2}
              maxLength={60}
              data-testid="brands-form-name"
              className="bg-surface border-border-subtle"
              autoFocus
            />
          </div>
        </div>
      </FormModal>

      <ConfirmDeleteModal
        open={Boolean(crud.deleteRow)}
        onClose={crud.cancelDelete}
        onConfirm={crud.confirmDelete}
        name={crud.deleteRow?.name || ""}
        busy={crud.deleting}
        testId="brands-delete"
      />
    </>
  );
}
